/**
 * Lee del archivo DICOM lo que el visor necesita para decidir que ofrece.
 *
 * @module presentation/components/dicom/leer-caracteristicas
 */

import { metaData } from '@cornerstonejs/core'
import type { CaracteristicasDeLaImagen, Ventana } from '@/domain/visor'

const SIN_VENTANA_PROPIA: readonly Ventana[] = []

const REVELADAS = ['RGB', 'PALETTE COLOR', 'YBR_FULL', 'YBR_FULL_422', 'YBR_RCT', 'YBR_ICT']

const comoLista = (valor: unknown): number[] => {
  if (Array.isArray(valor)) return valor.map(Number).filter((n) => Number.isFinite(n))
  const numero = Number(valor)
  return Number.isFinite(numero) ? [numero] : []
}

const ventanasDeclaradas = (centros: number[], anchos: number[]): readonly Ventana[] => {
  const cuantas = Math.min(centros.length, anchos.length)
  if (cuantas === 0) return SIN_VENTANA_PROPIA

  return Array.from({ length: cuantas }, (_, i) => ({
    nombre: cuantas === 1 ? 'Del estudio' : `Del estudio ${i + 1}`,
    centro: centros[i],
    ancho: anchos[i],
  })).filter((ventana) => ventana.ancho > 0)
}

export const leerCaracteristicas = (
  imageId: string,
  cortes: number
): CaracteristicasDeLaImagen => {
  const serie = metaData.get('generalSeriesModule', imageId) ?? {}
  const voi = metaData.get('voiLutModule', imageId) ?? {}
  const rescalado = metaData.get('modalityLutModule', imageId) ?? {}
  const plano = metaData.get('imagePlaneModule', imageId) ?? {}
  const pixeles = metaData.get('imagePixelModule', imageId) ?? {}
  const calibracion = metaData.get('calibrationModule', imageId) ?? {}

  const modalidad = serie.modality ?? null
  const tipoDeRescalado = (rescalado.rescaleType ?? '').toUpperCase()
  const interpretacion = (pixeles.photometricInterpretation ?? '').toUpperCase()

  return {
    modalidad,
    enHounsfield: tipoDeRescalado === 'HU' || modalidad === 'CT',
    ventanasDelArchivo: ventanasDeclaradas(
      comoLista(voi.windowCenter),
      comoLista(voi.windowWidth)
    ),
    yaRevelada: modalidad === 'US' || REVELADAS.includes(interpretacion),
    calibradaAlPaciente:
      plano.usingDefaultValues === false ||
      (calibracion.sequenceOfUltrasoundRegions?.length ?? 0) > 0,
    cortes,
  }
}
